import { groupBy } from 'es-toolkit';

import {
  DirectedAcyclicGraph,
  getUserVisibleOrDefaultExceptionMessage,
  isUserVisibleException,
} from '@paragon/client-tools';
import {
  AnalysisHistoryRecord,
  AnalysisOptions,
  AnalysisSet,
  ComponentDesign,
  ComponentDesignTimestamps,
  ConnectingHardware,
  Excavation,
  UsageData,
} from '@paragon/contract-library';
import { designService } from '@paragon/data-connections';
import {
  getLoggedInUser,
  hasAnalysisEvaluationPermission,
  hasCanUsePlatesPermission,
  hasHangerUpgradeAndAnalyzeFeatureFlag,
  hasTrussDesignPermission,
  isLoggedInUserRoot,
} from '@paragon/login-tools';
import { closeAllSnackbars, enqueueError, enqueueInfo } from '@paragon/notification-tools';
import { addSpinnerMessage, removeSpinnerMessage } from '@paragon/task-spinner';
import { assertExhaustiveSwitch, sortBy, updateToDeep } from '@paragon/utilities';

import { getAppMonitor } from '../../monitor';
import { RootState } from '../index';
import { waitForComponentDesignToFinishSaving } from '../middlewares/saveComponentDesignMiddleware';
import { getIsRecordingCanaries } from '../selectors/canaryDownloadSelectors';
import { getActiveComponentDesign } from '../selectors/getActiveComponentDesignSelector';
import { getSingleSelectedItemEntryOrNull } from '../selectors/getSelectedItemSelector';
import { isParagonJobOpen } from '../selectors/jobContextSelectors';
import { isLayoutViewActive } from '../selectors/layoutModeSelectors';
import { isLongRunningAnalysisOngoing } from '../selectors/longRunningOperationSelectors';
import { createDoWorkingChangesExistOnComponentDesign } from '../selectors/workingChangesSelectors';
import { onCompletingAsyncRequest, onInitiatingAnalyzeRequest } from '../slices/asyncRequestsSlice';
import { addCanaryExcavation, addCanaryExcavations, startCanaryRecording } from '../slices/canaryDownloadSlice';
import { setComponentDesignTimestamps } from '../slices/componentDesignTimestampsSlice';
import { setJackGirderConnections } from '../slices/jackGirderConnectionsSlice';
import { setLoadSets } from '../slices/loadSetsSlice';
import {
  clearLongRunningAnalysis,
  recordErrorMessageDuringLongRunningAnalysis,
  startLongRunningAnalysis,
} from '../slices/longRunningOperationsSlice';
import { usageDataLoaded } from '../slices/usageDataSlice';
import { closeSettingsPanel, setUnsavedComponentDesignFromServer } from './appActions';
import { generateTddInJob } from './generateTddActions';
import { loadAdditionalPlatePairInformation, loadAnalysisResponse } from './loadActions';
import { cancelActiveAnalysisResultRequests } from './loadAnalysisResults';
import {
  recordErrorDuringAnalysis,
  updateComponentDesignStageInLongRunningAnalysis,
} from './longRunningAnalysisActions';
import { showUpgradeAccountDialogIfQuotaMetAndNotAllowedToExceed } from './popupActions';
import { selectItem } from './selectActions';
import { AsyncThunkActionVoid, ThunkActionVoid } from './thunkActions';
import {
  warnIfComponentIsNotBuildable,
  warnIfDeflectionCreepFactorTooLow,
  warnIfEnvelopeProcedureUsedOnHighRise,
  warnIfIllegalUserPurlinSpacing,
  warnIfPlatingJsiLimitIsExceeded,
  warnIfSplicePlatesTooSmall,
} from './validationActions';

/**
 * Configurable analysis action options include some of the options that are passed to the route, as well as other
 * options that are relevant to the analysis process, like selecting when finished. Some options passed to the route,
 * like `logVariables` and `abbreviated` are excluded because these are derivable from the Redux state and therefore
 * should not be set except right next to the route call. If there is a future use case where it needs to be set at
 * a higher level, they can be included in the Pick<>.
 */
interface AnalysisActionOptions extends Pick<AnalysisOptions, 'commitName'> {
  /**
   * Controls whether the component design being analyzed will be selected as soon as analysis is complete.
   */
  selectWhenFinished: boolean | 'ifComponentDesignIsActive';
  /**
   * Controls whether to add a spinner message during analysis. Should be set to `false` if there is already another
   * active spinner or if the long-running analysis dialog is responsible for presenting progress to the user.
   */
  addSpinner: boolean;
}

const MAX_ANALYSIS_TASKS = 3;

export const bulkUpgradeAndAnalyze =
  (componentDesignGuids: string[]): AsyncThunkActionVoid =>
  async (dispatch, getState) => {
    // Check if this bulk analysis will cause the user's quota to be exceeded. Components that have not been analyzed
    // before (do not have a history) will produce Analysis events which count against the quota. Components that
    // have been analyzed will also produce Analysis events, but these will not count against the quota.
    const analysesThatWillCountAgainstTheQuota = componentDesignGuids.filter(
      (componentDesignGuid) => getState().analysisHistory[componentDesignGuid],
    ).length;
    const dialogShown = dispatch(
      showUpgradeAccountDialogIfQuotaMetAndNotAllowedToExceed(
        analysesThatWillCountAgainstTheQuota,
        'Analyzing all trusses will exceed your quota. Consider analyzing individually or upgrading your account.',
      ),
    );
    if (dialogShown) return;

    cancelActiveAnalysisResultRequests();

    const state = getState();

    const componentDesignGuidsSet = new Set(componentDesignGuids);
    const componentDesignsToAnalyze = Object.values(state.componentDesigns.data).filter((componentDesign) =>
      componentDesignGuidsSet.has(componentDesign.guid),
    );

    const graph = DirectedAcyclicGraph.fromComponentDesignsAndJackGirderConnections(
      componentDesignsToAnalyze,
      Object.values(state.jackGirderConnections.data).filter(
        (connection) =>
          componentDesignsToAnalyze.some((componentDesign) => componentDesign.guid === connection.girderGuid) &&
          componentDesignsToAnalyze.some((componentDesign) => componentDesign.guid === connection.jackGuid),
      ),
    );

    const groupedComponentDesigns = sortBy(
      Object.entries(
        groupBy(componentDesignsToAnalyze, (componentDesign) =>
          graph.getMaxDistanceToAnyRootVertex(componentDesign.guid),
        ),
      ),
      ([level, _]) => level,
      'desc',
    ).map(([_, componentDesigns]) => componentDesigns);

    dispatch(
      startLongRunningAnalysis({
        componentDesignGuids: groupedComponentDesigns.flat().map((componentDesign) => componentDesign.guid),
      }),
    );

    for (const componentDesignsThatCanBeAnalyzedTogether of groupedComponentDesigns) {
      const analysisTasks = [];
      const componentDesignStack = componentDesignsThatCanBeAnalyzedTogether.toReversed();

      for (let i = 0; i < MAX_ANALYSIS_TASKS; i++) {
        analysisTasks.push(dispatch(analyzeComponentDesignsFromStack(componentDesignStack)));
      }

      for (const task of analysisTasks) {
        await task;
      }

      if (getState().longRunningOperations.analysis?.canceled) {
        dispatch(clearLongRunningAnalysis());
        return;
      }
    }
  };

const analyzeComponentDesignsFromStack =
  (componentDesigns: ComponentDesign[]): AsyncThunkActionVoid =>
  async (dispatch, getState) => {
    while (componentDesigns.length > 0) {
      const componentDesign = componentDesigns.pop();
      if (componentDesign == null) break; // TODO

      try {
        await dispatch(
          upgradeAndAnalyze(componentDesign, {
            selectWhenFinished: 'ifComponentDesignIsActive',
            addSpinner: false,
          }),
        );

        if (getState().longRunningOperations.analysis?.canceled) {
          break;
        }
      } catch {
        // nom
      }
    }
  };

function getAnalysisOptions(options: AnalysisActionOptions, state: RootState): AnalysisOptions {
  const loggedInUser = getLoggedInUser(state);
  if (!loggedInUser) throw new Error('Expected user to be logged in.');
  return {
    ...options,
    commitName: `Analyzed by ${loggedInUser.firstName} ${loggedInUser.lastName}`,
    abbreviated: !state.application.detailedAnalysis,
    // There's no point in logging the variables if we're not going to save the analysis results
    logVariables: state.application.detailedAnalysis && hasAnalysisEvaluationPermission(state),
  };
}

export const analyzeComponentDesign =
  (componentDesign: ComponentDesign, options: AnalysisActionOptions): AsyncThunkActionVoid =>
  async (dispatch, getState) => {
    const analyzingWillAddToQuota = !getState().analysisHistory[componentDesign.guid];
    if (analyzingWillAddToQuota) {
      const dialogShown = dispatch(showUpgradeAccountDialogIfQuotaMetAndNotAllowedToExceed());
      if (dialogShown) return;
    }

    dispatch(updateComponentDesignStageInLongRunningAnalysis(componentDesign.guid, 'analyzing'));

    const spinnerId = options.addSpinner
      ? dispatch(addSpinnerMessage(`Analyzing ${componentDesign.component.name}...`))
      : null;

    const requestGuid = crypto.randomUUID();
    dispatch(onInitiatingAnalyzeRequest({ componentDesignGuid: componentDesign.guid, requestGuid }));

    let analysisSuccessful = false;

    try {
      await waitForComponentDesignToFinishSaving(componentDesign.guid);

      const analysisOptions = getAnalysisOptions(options, getState());
      const { analysisSet, analysisHistoryRecord, componentDesignTimestamps, excavation, trussDesignUsage } =
        await designService.componentDesigns.postAnalyze(componentDesign, {
          analysisOptions,
          recordCanaries: getIsRecordingCanaries(getState()),
          runPlateMislocations: getState().application.runPlateMislocations,
        });

      await dispatch(
        processAnalysisResponse(
          analysisSet,
          analysisHistoryRecord,
          componentDesignTimestamps,
          trussDesignUsage,
          options,
        ),
      );

      dispatch(addExcavation(excavation));

      analysisSuccessful = true;
    } catch (exception) {
      const message = getUserVisibleOrDefaultExceptionMessage(exception, 'Unable to analyze.');
      dispatch(recordErrorDuringAnalysis(componentDesign.guid, message, { fatal: true }));

      throw exception;
    } finally {
      dispatch(onCompletingAsyncRequest({ componentDesignGuid: componentDesign.guid, requestGuid }));
      if (spinnerId != null) dispatch(removeSpinnerMessage(spinnerId));

      dispatch(
        updateComponentDesignStageInLongRunningAnalysis(
          componentDesign.guid,
          analysisSuccessful ? 'finished' : 'failed',
        ),
      );
    }
  };

const processAnalysisResponse =
  (
    analysisSet: AnalysisSet,
    analysisHistoryRecord: AnalysisHistoryRecord,
    componentDesignTimestamps: ComponentDesignTimestamps,
    trussDesignUsage: UsageData,
    options: AnalysisActionOptions,
  ): AsyncThunkActionVoid =>
  async (dispatch, getState) => {
    const { componentDesign } = analysisSet;

    if (isLongRunningAnalysisOngoing(getState())) {
      for (const error of analysisSet.errors) {
        dispatch(
          recordErrorMessageDuringLongRunningAnalysis({
            componentDesignGuid: componentDesign.guid,
            type: 'error',
            message: error,
            fatal: false,
          }),
        );
      }

      for (const warning of analysisSet.warnings) {
        dispatch(
          recordErrorMessageDuringLongRunningAnalysis({
            componentDesignGuid: componentDesign.guid,
            type: 'warning',
            message: warning,
            fatal: false,
          }),
        );
      }
    }

    dispatch(setComponentDesignTimestamps({ timestamps: componentDesignTimestamps }));

    dispatch(usageDataLoaded(trussDesignUsage));

    dispatch(warnIfComponentIsNotBuildable(componentDesign));
    dispatch(warnIfIllegalUserPurlinSpacing(analysisSet));
    dispatch(warnIfPlatingJsiLimitIsExceeded(analysisSet));
    dispatch(warnIfSplicePlatesTooSmall(analysisSet));
    dispatch(warnIfDeflectionCreepFactorTooLow(analysisSet));
    dispatch(warnIfEnvelopeProcedureUsedOnHighRise(analysisSet));

    const activeComponentDesign = getActiveComponentDesign(getState());

    const layoutViewActive = isLayoutViewActive(getState());
    const componentDesignIsActive = activeComponentDesign?.guid === componentDesign.guid && !layoutViewActive;
    const selectWhenFinished =
      options.selectWhenFinished === 'ifComponentDesignIsActive' ? componentDesignIsActive : options.selectWhenFinished;

    await dispatch(loadAnalysisResponse(analysisSet, analysisHistoryRecord, selectWhenFinished));

    if (isParagonJobOpen(getState()) && getState().application.regenerateTddsAutomatically) {
      await dispatch(generateTddInJob(analysisSet.analysisReportGuid, componentDesign.guid));
    }
  };

export async function anyJacksHaveWorkingChanges(getState: () => RootState, girderGuid: string): Promise<boolean> {
  const jackConnections = Object.values(getState().jackGirderConnections.data).filter(
    (connection) => connection.girderGuid === girderGuid,
  );

  for (const jackConnection of jackConnections) {
    await waitForComponentDesignToFinishSaving(jackConnection.jackGuid);
    const jackHasWorkingChanges = createDoWorkingChangesExistOnComponentDesign(jackConnection.jackGuid);
    if (jackHasWorkingChanges(getState())) {
      return true;
    }
  }

  return false;
}

export const upgradeAndAnalyzeActiveComponentDesign = (): AsyncThunkActionVoid => async (dispatch, getState) => {
  const state = getState();

  const activeComponentDesign = getActiveComponentDesign(state);
  if (!activeComponentDesign) throw new Error('Could not find active component.');

  const analyzingWillAddToQuota = !getState().analysisHistory[activeComponentDesign.guid];
  if (analyzingWillAddToQuota) {
    const dialogShown = dispatch(showUpgradeAccountDialogIfQuotaMetAndNotAllowedToExceed());
    if (dialogShown) return;
  }

  await dispatch(
    upgradeAndAnalyze(activeComponentDesign, { selectWhenFinished: 'ifComponentDesignIsActive', addSpinner: true }),
  );
};

const upgradeAndAnalyze =
  (componentDesign: ComponentDesign, options: AnalysisActionOptions): AsyncThunkActionVoid =>
  async (dispatch, getState) => {
    if (await anyJacksHaveWorkingChanges(getState, componentDesign.guid)) {
      dispatch(
        recordErrorDuringAnalysis(
          componentDesign.guid,
          'Unable to generate load cases: Some jack components have unanalyzed changes.',
          { fatal: true },
        ),
      );
      return;
    }

    const timer = getAppMonitor().beginTimedOperation('Upgrade and Analyze');

    const spinnerId = options.addSpinner
      ? dispatch(addSpinnerMessage(`Upgrading and Analyzing ${componentDesign.component.name}...`))
      : null;

    const requestGuid = crypto.randomUUID();
    dispatch(onInitiatingAnalyzeRequest({ componentDesignGuid: componentDesign.guid, requestGuid }));

    let upgradeAndAnalyzeSuccessful = false;

    try {
      cancelActiveAnalysisResultRequests();
      dispatch(closeAllSnackbars());

      dispatch(updateComponentDesignStageInLongRunningAnalysis(componentDesign.guid, 'upgrading-and-analyzing'));

      await waitForComponentDesignToFinishSaving(componentDesign.guid);

      const analysisOptions = getAnalysisOptions(options, getState());
      const upgradeAndAnalyzeResponse = await designService.componentDesigns.postUpgradeAndAnalyze(componentDesign, {
        analysisOptions,
        recordCanaries: getIsRecordingCanaries(getState()),
        runPlateMislocations: getState().application.runPlateMislocations,
        runExhaustivePlateSearch: getState().application.runExhaustivePlateSearch,
        runOptimizations: getState().application.runOptimizations,
        allowOptimizationChordResizing: getState().application.allowOptimizationChordResizing,
        selectHangers: hasHangerUpgradeAndAnalyzeFeatureFlag(getState()),
        memberOnlyAnalysis: !hasCanUsePlatesPermission(getState()) && componentDesign.component.platePairs.length === 0,
        optimizePlating: true,
        maxOptimizationPlies: null,
        varySplicing: true,
        costWeights: null,
        userSpecifiedLumberPrices: null,
        userSpecifiedPlatePrices: null,
      });

      dispatch(setJackGirderConnections(upgradeAndAnalyzeResponse.updatedJackGirderConnections));

      dispatch(addExcavations(upgradeAndAnalyzeResponse.excavations));

      for (const errorMessage of upgradeAndAnalyzeResponse.errorMessages) {
        dispatch(recordErrorDuringAnalysis(componentDesign.guid, errorMessage, { fatal: false }));
      }

      if (upgradeAndAnalyzeResponse.loadSet) {
        dispatch(setLoadSets(upgradeAndAnalyzeResponse.loadSet));
      }

      if (upgradeAndAnalyzeResponse.result === 'SuccessfullyAnalyzed') {
        const { analysisSet, analysisHistoryRecord, componentDesignTimestamps, trussDesignUsage } =
          upgradeAndAnalyzeResponse;
        await dispatch(
          processAnalysisResponse(
            analysisSet,
            analysisHistoryRecord,
            componentDesignTimestamps,
            trussDesignUsage,
            options,
          ),
        );
        upgradeAndAnalyzeSuccessful = true;
      } else if (upgradeAndAnalyzeResponse.result === 'FailedBeforeFinishingAnalysis') {
        if (upgradeAndAnalyzeResponse.componentDesign) {
          dispatch(updateUnsavedComponentDesignFromServer(upgradeAndAnalyzeResponse.componentDesign));
        }

        if (upgradeAndAnalyzeResponse.componentDesignTimestamps) {
          dispatch(setComponentDesignTimestamps({ timestamps: upgradeAndAnalyzeResponse.componentDesignTimestamps }));
        }
      } else {
        assertExhaustiveSwitch(upgradeAndAnalyzeResponse);
      }
    } catch (exception) {
      const message = getUserVisibleOrDefaultExceptionMessage(exception, 'Unable to analyze.');
      dispatch(recordErrorDuringAnalysis(componentDesign.guid, message, { fatal: true }));

      const selectedEntry = getSingleSelectedItemEntryOrNull(getState());
      const layoutViewActive = isLayoutViewActive(getState());
      const selectWhenFinished =
        !(selectedEntry?.type === 'componentDesign' && selectedEntry.item.guid === componentDesign.guid) &&
        (options.selectWhenFinished === 'ifComponentDesignIsActive' ? !layoutViewActive : options.selectWhenFinished);
      if (selectWhenFinished) {
        dispatch(selectItem(componentDesign, 'componentDesign', { force: true, retainNotifications: true }));
      }
    } finally {
      dispatch(onCompletingAsyncRequest({ componentDesignGuid: componentDesign.guid, requestGuid }));
      if (spinnerId != null) dispatch(removeSpinnerMessage(spinnerId));

      dispatch(
        updateComponentDesignStageInLongRunningAnalysis(
          componentDesign.guid,
          upgradeAndAnalyzeSuccessful ? 'finished' : 'failed',
        ),
      );

      getAppMonitor().concludeTimedOperation(timer);
    }
  };

const updateUnsavedComponentDesignFromServer =
  (newComponentDesign: ComponentDesign): ThunkActionVoid =>
  (dispatch, getState) => {
    const existingComponentDesign = getState().componentDesigns.data[newComponentDesign.guid];
    const updatedComponentDesign = updateToDeep(existingComponentDesign, newComponentDesign);

    if (existingComponentDesign === updatedComponentDesign) return;

    dispatch(setUnsavedComponentDesignFromServer(updatedComponentDesign));
  };

export const generateAnalogModel =
  (componentDesign: ComponentDesign): AsyncThunkActionVoid =>
  async (dispatch, getState) => {
    const spinnerId = dispatch(addSpinnerMessage(`Generating analog model for ${componentDesign.component.name}...`));

    try {
      const { componentDesign: updatedComponentDesign, excavation } =
        await designService.componentDesigns.postGenerateAnalogModel(
          componentDesign,
          getIsRecordingCanaries(getState()),
        );
      dispatch(addExcavation(excavation));

      dispatch(updateUnsavedComponentDesignFromServer(updatedComponentDesign));
    } catch (exception) {
      dispatch(enqueueError(getUserVisibleOrDefaultExceptionMessage(exception, `Unable to generate analog model.`)));
    } finally {
      dispatch(removeSpinnerMessage(spinnerId));
    }
  };

export const generateLoadCasesOfActiveComponentDesign = (): AsyncThunkActionVoid => async (dispatch, getState) => {
  const activeComponentDesign = getActiveComponentDesign(getState());
  if (!activeComponentDesign) return;

  await dispatch(generateLoadCases(activeComponentDesign));
  dispatch(closeSettingsPanel());
};

export const generateLoadCases =
  (componentDesign: ComponentDesign): AsyncThunkActionVoid =>
  async (dispatch, getState) => {
    if (await anyJacksHaveWorkingChanges(getState, componentDesign.guid)) {
      dispatch(enqueueError('Unable to generate load cases: Some jack components have unanalyzed changes.'));
      return;
    }

    const spinnerId = dispatch(addSpinnerMessage(`Generating load cases for ${componentDesign.component.name}...`));

    try {
      const {
        componentDesign: updatedComponentDesign,
        excavation,
        loadSet,
      } = await designService.componentDesigns.postGenerateLoadCases(
        componentDesign,
        getIsRecordingCanaries(getState()),
      );
      dispatch(addExcavation(excavation));

      dispatch(setLoadSets(loadSet));
      dispatch(updateUnsavedComponentDesignFromServer(updatedComponentDesign));
    } catch (exception) {
      const message = getUserVisibleOrDefaultExceptionMessage(exception, `Unable to generate load cases.`);
      dispatch(enqueueError(`Unable to generate load cases: ${message}`));
    } finally {
      dispatch(removeSpinnerMessage(spinnerId));
    }
  };

export const upgradeMembers =
  (componentDesign: ComponentDesign): AsyncThunkActionVoid =>
  async (dispatch, getState) => {
    const spinnerId = dispatch(addSpinnerMessage('Upgrading members...'));

    try {
      const { componentDesign: upgradedComponentDesign, excavation } =
        await designService.componentDesigns.postUpgradeMembers(componentDesign, getIsRecordingCanaries(getState()));
      dispatch(addExcavation(excavation));
      dispatch(updateUnsavedComponentDesignFromServer(upgradedComponentDesign));
    } catch (error) {
      const messageEnd = isUserVisibleException(error) ? ` ${error.userVisibleMessage}` : '';
      dispatch(enqueueError(`Error upgrading members.${messageEnd}`));
    } finally {
      dispatch(removeSpinnerMessage(spinnerId));
    }
  };

export const generatePlates =
  (componentDesign: ComponentDesign): AsyncThunkActionVoid =>
  async (dispatch, getState) => {
    const spinnerId = dispatch(addSpinnerMessage(`Plating ${componentDesign.component.name}...`));

    try {
      const { componentDesign: updatedComponentDesign, excavation } =
        await designService.componentDesigns.postGeneratePlates(
          componentDesign,
          getState().application.runExhaustivePlateSearch,
          getIsRecordingCanaries(getState()),
        );
      dispatch(addExcavation(excavation));

      await dispatch(loadAdditionalPlatePairInformation(updatedComponentDesign.component.platePairs));
      dispatch(updateUnsavedComponentDesignFromServer(updatedComponentDesign));
    } catch (exception) {
      const messageEnd = isUserVisibleException(exception) ? ` ${exception.userVisibleMessage}` : '';
      dispatch(enqueueError(`Error while plating.${messageEnd}`));
    } finally {
      dispatch(removeSpinnerMessage(spinnerId));
    }
  };

export const autoSplice =
  (componentDesign: ComponentDesign): AsyncThunkActionVoid =>
  async (dispatch) => {
    const spinnerId = dispatch(addSpinnerMessage('Auto-Splicing...'));

    try {
      const updatedComponentDesign = await designService.componentDesigns.postAutoSplice(componentDesign);
      dispatch(updateUnsavedComponentDesignFromServer(updatedComponentDesign));
    } catch (exception) {
      const messageEnd = isUserVisibleException(exception) ? ` ${exception.userVisibleMessage}` : '';
      dispatch(enqueueError(`Error while auto-splicing.${messageEnd}`));
    } finally {
      dispatch(removeSpinnerMessage(spinnerId));
    }
  };

export const deleteHangers =
  (componentDesign: ComponentDesign, guidsOfHangersToDelete: readonly string[]): AsyncThunkActionVoid =>
  async (dispatch) => {
    const spinnerId = dispatch(addSpinnerMessage('Deleting Hangers...'));

    try {
      const { updatedComponentDesign, jackGirderConnections } = await designService.componentDesigns.deleteHangers(
        componentDesign,
        guidsOfHangersToDelete,
      );
      dispatch(updateUnsavedComponentDesignFromServer(updatedComponentDesign));
      dispatch(setJackGirderConnections(jackGirderConnections));
    } catch (exception) {
      dispatch(enqueueError('Error while deleting hangers'));
    } finally {
      dispatch(removeSpinnerMessage(spinnerId));
    }
  };

export const selectHangers =
  (componentDesign: ComponentDesign): AsyncThunkActionVoid =>
  async (dispatch, getState) => {
    if (await anyJacksHaveWorkingChanges(getState, componentDesign.guid)) {
      dispatch(enqueueError('Unable to select hangers: Some jack components have unanalyzed changes.'));
      return;
    }

    const spinnerId = dispatch(addSpinnerMessage('Selecting Hangers...'));

    try {
      const { changes } = await designService.componentDesigns.postSelectHangers(componentDesign);

      if (changes == null) {
        dispatch(enqueueInfo('No connections required hangers.'));
      } else {
        dispatch(updateUnsavedComponentDesignFromServer(changes.updatedComponentDesign));
        dispatch(setJackGirderConnections(changes.jackGirderConnections));
      }
    } catch (exception) {
      const failureMessageStart = 'Error while selecting hangers';
      const failureMessage = isUserVisibleException(exception)
        ? `${failureMessageStart}: ${exception.userVisibleMessage}`
        : `${failureMessageStart}.`;
      dispatch(enqueueError(failureMessage));
    } finally {
      dispatch(removeSpinnerMessage(spinnerId));
    }
  };

export const changeHangers =
  (componentDesign: ComponentDesign, hangerGuid: string, newConnector: ConnectingHardware): AsyncThunkActionVoid =>
  async (dispatch) => {
    const spinnerId = dispatch(addSpinnerMessage('Updating Hanger...'));

    try {
      const { updatedComponentDesign, jackGirderConnections } = await designService.componentDesigns.postChangeHangers(
        componentDesign,
        hangerGuid,
        newConnector,
      );
      dispatch(updateUnsavedComponentDesignFromServer(updatedComponentDesign));
      dispatch(setJackGirderConnections(jackGirderConnections));
    } catch (exception) {
      dispatch(enqueueError('Error while changing hanger.'));
    } finally {
      dispatch(removeSpinnerMessage(spinnerId));
    }
  };

const hasPermissionsToRecordCanaries = (state: RootState) =>
  isLoggedInUserRoot(state) && hasTrussDesignPermission(state);

export const startCanaryRecordingIfHasPermissions = (): ThunkActionVoid => (dispatch, getState) => {
  if (!hasPermissionsToRecordCanaries(getState())) return;
  dispatch(startCanaryRecording());
};

const shouldSaveRecordedCanaries = (state: RootState) =>
  hasPermissionsToRecordCanaries(state) && getIsRecordingCanaries(state);

const addExcavation =
  (excavation: Excavation | null): ThunkActionVoid =>
  (dispatch, getState) => {
    if (!excavation || !shouldSaveRecordedCanaries(getState())) return;
    dispatch(addCanaryExcavation(excavation));
  };

const addExcavations =
  (excavations: readonly Excavation[]): ThunkActionVoid =>
  (dispatch, getState) => {
    if (!shouldSaveRecordedCanaries(getState())) return;
    dispatch(addCanaryExcavations(excavations));
  };
